import { useState } from 'react'
import {
  Container,
  VStack,
  Box,
  Heading,
  Text,
  HStack,
  Badge,
  Spinner,
  Alert,
} from '@chakra-ui/react';
import { useAdminUsers } from '../hooks/useAdminUsers'
import { UserEditModal } from '../components/admin/UserEditModal'
import { UserAvatar } from '../components/admin/UserAvatar'
import { ResponsiveTable, type ResponsiveColumn } from '../components/shared'
import type { AdminUser } from '../types/admin'

const roleBadgeColor: Record<string, string> = {
  Admin: 'red',
  Organizer: 'purple',
  Canvasser: 'blue',
}

const columns: ResponsiveColumn<AdminUser>[] = [
  {
    header: 'Name',
    mobileRole: 'title',
    cell: (user) => (
      <HStack gap={3}>
        <UserAvatar
          userId={user.id}
          firstName={user.firstName}
          portraitUrl={user.portraitUrl}
          size="sm"
        />
        <Text fontWeight="medium">
          {user.lastName}, {user.firstName}
        </Text>
      </HStack>
    ),
  },
  {
    header: 'Email',
    mobileRole: 'meta',
    cell: (user) => <Text fontSize="sm">{user.email}</Text>,
  },
  {
    header: 'Role',
    mobileRole: 'badge',
    cell: (user) => (
      <Badge colorPalette={roleBadgeColor[user.role] || 'gray'}>{user.role}</Badge>
    ),
  },
  {
    header: 'Committee',
    mobileRole: 'meta',
    cell: (user) => (
      <Text fontSize="sm" color={user.committeeLabel ? 'gray.700' : 'gray.400'}>
        {user.committeeLabel || 'Unassigned'}
      </Text>
    ),
  },
  {
    header: 'Status',
    mobileRole: 'badge',
    cell: (user) => (
      <Badge colorPalette={user.isActive ? 'green' : 'gray'}>
        {user.isActive ? 'Active' : 'Inactive'}
      </Badge>
    ),
  },
  {
    header: 'Created',
    mobileRole: 'desktop-only',
    cell: (user) => (
      <Text fontSize="sm" color="gray.500">
        {new Date(user.createdAt).toLocaleDateString()}
      </Text>
    ),
  },
]

export function AdminUsersPage() {
  const { data: users, isLoading, error } = useAdminUsers()
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null)

  return (
    <Container maxW="container.xl" py={8}>
      <VStack gap={6} align="stretch">
        <Box>
          <Heading size="lg">User Management</Heading>
          <Text color="gray.500" mt={1}>
            Manage user accounts, roles, and committee assignments
          </Text>
        </Box>

        {isLoading && (
          <VStack py={12}>
            <Spinner size="lg" color="accent" />
            <Text color="gray.500">Loading users...</Text>
          </VStack>
        )}

        {!!error && (
          <Alert.Root status="error" borderRadius="md">
            <Alert.Indicator />
            Failed to load users. Please try again.
          </Alert.Root>
        )}

        {!isLoading && users && users.length > 0 && (
          <ResponsiveTable
            columns={columns}
            rows={users}
            keyFn={(user) => user.id}
            onRowClick={(user) => setEditingUser(user)}
          />
        )}

        {!isLoading && users && users.length === 0 && (
          <Box bg="surface" p={12} borderRadius="lg" shadow="md" textAlign="center">
            <Text color="gray.500">No users found.</Text>
          </Box>
        )}
      </VStack>

      <UserEditModal
        user={editingUser}
        isOpen={editingUser !== null}
        onClose={() => setEditingUser(null)}
      />
    </Container>
  );
}
